import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useCreateDialog, useEditRoute } from '@/contexts/CreateDialogContext'
import posthog from 'posthog-js'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useStore } from '@tanstack/react-store'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Check, Plus, IdCard, Mail, Loader2, TriangleAlert, ShoppingBag, LayoutList, Image, Pencil, X, Tag, Landmark, Globe, Building2, Users, User, Phone, FileText, Coins, Link2, Clock, MapPin, Receipt } from 'lucide-react'
import { BulkActionBar } from '../../components/BulkActionBar'

import { CreateObjectButton } from '@/components/CreateObjectButton'
import { Button } from '@/components/ui/button'
import { SearchSelect } from '@/components/SearchSelect'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  StatsBar,
  StatsBarId,
  StatsBarActivity,
  StatsBarTraffic,
} from '../../components/StatsBar'
import { AuthService } from '@/services/core/auth'
import { AdvertisersApi, AdvertiserInput, Advertiser } from '@/services/advertisers/advertisersApi'
import { TeamsApi } from '@/services/users/teamsApi'
import { OrdersApi, Order } from '@/services/orders/ordersApi'
import { LineItemsApi, LineItem } from '@/services/lineItems/lineItemsApi'
import { CreativesApi, Creative } from '@/services/creatives/creativesApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { OrderTable } from '../../components/OrderTable'
import { LineItemTable } from '../../components/LineItemTable'
import { CreativeTable } from '../../components/CreativeTable'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { AppShell } from '../../components/AppShell'
import { FormSkeleton } from '../../components/FormSkeleton'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { useEditMode } from '../../hooks/useEditMode'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { LabelAutocomplete } from '../../components/LabelAutocomplete'
import { Switch } from '@/components/ui/switch'
import { StatusFilterButton } from '../../components/StatusFilter'
import { ListTabs, type ListTab } from '../../components/ListTabs'
import { ListCard, BulkActionsSlot, ColumnsSlot, TableActionsSlot } from '../../components/ListCard'
import { LineItemFilters, useLineItemFilters } from '../../components/LineItemFilters'
import { StatsPanel } from '../../components/StatsPanel'
import { Card, CardHeader, CardIcon, CardTitle, CardContent } from '@/components/ui/card'
import { ListCardHeader } from '@/components/ListCardHeader'
import { AdvertiserTransparencyCard, useLinkedSponsorNames } from '@/components/political/CampaignTransparency'
import { PoliticalBadge } from '@/components/political/PoliticalBadge'
import { DetailList, DetailRow } from '../../components/DetailList'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { usePersistedState } from '@/hooks/usePersistedState'
import { SearchInput } from '../../components/SearchInput'
import { filterBySearch } from '../../utils/search'
import { useObjectCrumbs } from '../../hooks/useObjectCrumbs'
import { formatDate } from '../../utils/format'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { ElectionsApi } from '@/services/political/electionsApi'
import { ELECTIONS_QUERY_KEY } from '../../components/political/ElectionSelect'
import { AdvertiserInviteDialog, politicalLinkRequestQueryKey } from '../../components/AdvertiserInviteDialog'
import { AdvertiserPoliticalLinkDialog } from '../../components/AdvertiserPoliticalLinkDialog'
import { ValidationWarnings } from '@/components/ValidationWarnings'
import { ObjectStateToggle, useSaveObjectState, type ObjectState } from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { AdvancedSection } from '../../components/AdvancedSection'
import { AdvertiserPoliticalBudgetsEditor } from '@/components/political/AdvertiserPoliticalBudgetsEditor'
import { AdvertiserEntityFields } from '@/components/political/AdvertiserEntityFields'
import { AdvertiserPoliticalSummaryCard } from '@/components/political/AdvertiserPoliticalSummaryCard'
import { useNetwork } from '@/contexts/NetworkContext'
import {
  budgetDrafts,
  budgetIssues,
  budgetsInput,
  entitiesInput,
  entityDraft,
  ENTITY_TYPES,
  type EntityDraft,
} from '@/utils/advertiserPolitical'

const posthogConfigured = Boolean(
  import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN && import.meta.env.VITE_PUBLIC_POSTHOG_HOST
)

// ── Related panel (tabbed) ──────────────────────────────────────────────────

const RELATED_TABS = ['orders', 'lineitems', 'creatives'] as const
type RelatedTab = (typeof RELATED_TABS)[number]

function AdvertiserRelatedPanel({ advertiserId, ownerTeamId }: { advertiserId: string; ownerTeamId?: string }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<RelatedTab>('orders')
  const [statusFilter, setStatusFilter] = usePersistedState<'all' | 'ACTIVE' | 'INACTIVE'>('filters.advertiserOrders.status', 'all')
  const [creativeStatusFilter, setCreativeStatusFilter] = usePersistedState<'all' | 'ACTIVE' | 'INACTIVE'>('filters.advertiserCreatives.status', 'all')
  const liFilters = useLineItemFilters({ objectState: 'all' }, 'advertiserLineItems')
  const [orderSelection, setOrderSelection] = useState<Order[]>([])
  const [lineItemSelection, setLineItemSelection] = useState<LineItem[]>([])
  const [creativeSelection, setCreativeSelection] = useState<Creative[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)
  const [search, setSearch] = useState('')

  const { data: orders = [], isLoading: ordersLoading } = useQuery({
    queryKey: ['advertiser-orders', advertiserId],
    queryFn: () => OrdersApi.getByAdvertiser(advertiserId),
  })
  const { data: lineItems = [], isLoading: lineItemsLoading } = useQuery({
    queryKey: ['advertiser-lineitems', advertiserId],
    queryFn: () => LineItemsApi.getByAdvertiser(advertiserId),
  })
  const { data: creatives = [], isLoading: creativesLoading } = useQuery({
    queryKey: ['advertiser-creatives', advertiserId],
    queryFn: () => CreativesApi.getByAdvertiser(advertiserId),
  })

  const tabConfig: ListTab<RelatedTab>[] = [
    { key: 'orders', icon: <ShoppingBag className="size-3.5" />, label: t('advertisers.ordersTab'), count: orders.length },
    { key: 'lineitems', icon: <LayoutList className="size-3.5" />, label: t('advertisers.lineItemsTab'), count: lineItems.length },
    { key: 'creatives', icon: <Image className="size-3.5" />, label: t('advertisers.creativesTab'), count: creatives.length },
  ]

  /**
   * Every tab activates and deactivates the same way — the only difference is
   * which api the rows go back to and which list has to be refetched after.
   */
  const bulkSetStatus = async (
    state: 'ACTIVE' | 'INACTIVE',
    rows: { id: string; name: string }[],
    update: (id: string, input: { name: string; objectState: 'ACTIVE' | 'INACTIVE' }) => Promise<unknown>,
    queryKey: string,
    clearSelection: () => void,
  ) => {
    setBulkLoading(true)
    try {
      await Promise.all(rows.map((row) => update(row.id, { name: row.name, objectState: state })))
      queryClient.invalidateQueries({ queryKey: [queryKey, advertiserId] })
      toast.success(t('common.done'), { duration: 3000 })
      clearSelection()
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
    } finally {
      setBulkLoading(false)
    }
  }

  const bulkSetOrderStatus = (state: 'ACTIVE' | 'INACTIVE') =>
    bulkSetStatus(state, orderSelection, OrdersApi.update, 'advertiser-orders', () => setOrderSelection([]))
  const bulkSetLineItemStatus = (state: 'ACTIVE' | 'INACTIVE') =>
    bulkSetStatus(state, lineItemSelection, LineItemsApi.update, 'advertiser-lineitems', () => setLineItemSelection([]))
  const bulkSetCreativeStatus = (state: 'ACTIVE' | 'INACTIVE') =>
    bulkSetStatus(state, creativeSelection, CreativesApi.update, 'advertiser-creatives', () => setCreativeSelection([]))

  const filteredOrders = filterBySearch(statusFilter === 'all' ? orders : orders.filter((o) => o.objectState === statusFilter), search)
  const filteredLineItems = filterBySearch(liFilters.applyFilters(lineItems), search)
  const filteredCreatives = filterBySearch(creativeStatusFilter === 'all' ? creatives : creatives.filter((c) => c.objectState === creativeStatusFilter), search)

  return (
    <ListCard>
      <CardHeader className="flex flex-row flex-wrap items-center gap-3">
        <BulkActionsSlot />
        <ListTabs tabs={tabConfig} value={activeTab} onValueChange={setActiveTab} />
        {activeTab === 'lineitems' ? (
          <LineItemFilters
            filters={liFilters.filters}
            onChange={liFilters.setFilters}
            defaults={liFilters.defaults}
            showAdvertiser={false}
          />
        ) : activeTab === 'creatives' ? (
          <StatusFilterButton value={creativeStatusFilter} onChange={setCreativeStatusFilter} defaultValue="all" />
        ) : (
          <StatusFilterButton value={statusFilter} onChange={setStatusFilter} defaultValue="all" />
        )}
        <SearchInput value={search} onChange={setSearch} />
        <ColumnsSlot />
        <div className="ml-auto flex items-center gap-2">
          {activeTab === 'orders' && (
            <CreateObjectButton type="order" context={{ advertiser: advertiserId, team: ownerTeamId }} />
          )}
          <TableActionsSlot />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {activeTab === 'orders' && (
          <>
            {orderSelection.length > 0 && (
              <BulkActionBar
                count={orderSelection.length}
                loading={bulkLoading}
                onActivate={() => bulkSetOrderStatus('ACTIVE')}
                onDeactivate={() => bulkSetOrderStatus('INACTIVE')}
                onClear={() => setOrderSelection([])}
              />
            )}
            <OrderTable
              orders={filteredOrders}
              loading={ordersLoading}
              showAdvertiser={false}
              emptyMessage={t('advertisers.noOrders')}
              selection={orderSelection}
              onSelectionChange={setOrderSelection}
            />
          </>
        )}
        {activeTab === 'lineitems' && (
          <>
            {lineItemSelection.length > 0 && (
              <BulkActionBar
                count={lineItemSelection.length}
                loading={bulkLoading}
                onActivate={() => bulkSetLineItemStatus('ACTIVE')}
                onDeactivate={() => bulkSetLineItemStatus('INACTIVE')}
                onClear={() => setLineItemSelection([])}
              />
            )}
            <LineItemTable
              lineItems={filteredLineItems}
              loading={lineItemsLoading}
              showAdvertiser={false}
              emptyMessage={t('advertisers.noLineItems')}
              selection={lineItemSelection}
              onSelectionChange={setLineItemSelection}
            />
          </>
        )}
        {activeTab === 'creatives' && (
          <>
            {creativeSelection.length > 0 && (
              <BulkActionBar
                count={creativeSelection.length}
                loading={bulkLoading}
                onActivate={() => bulkSetCreativeStatus('ACTIVE')}
                onDeactivate={() => bulkSetCreativeStatus('INACTIVE')}
                onClear={() => setCreativeSelection([])}
              />
            )}
            <CreativeTable
              creatives={filteredCreatives}
              loading={creativesLoading}
              showAdvertiser={false}
              emptyMessage={t('advertisers.noCreatives')}
              selection={creativeSelection}
              onSelectionChange={setCreativeSelection}
            />
          </>
        )}
      </CardContent>
    </ListCard>
  )
}

// ── Inner form component ────────────────────────────────────────────────────

interface AdvertiserEditFormProps {
  advertiser: Advertiser | null
  id: string
  isNew: boolean
}

function AdvertiserEditForm({ advertiser, id, isNew }: AdvertiserEditFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const createDialog = useCreateDialog()
  // A new object's cancel closes the create dialog, or leaves its page.
  const cancelNew = (path: string) => (createDialog ? createDialog.close() : navigate(path))
  const queryClient = useQueryClient()
  const { isEditing, startEditing, stopEditing } = useEditMode(isNew)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [linkedOpen, setLinkedOpen] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const { data: elections = [] } = useQuery({
    queryKey: ELECTIONS_QUERY_KEY,
    queryFn: ElectionsApi.getAll,
    staleTime: 5 * 60 * 1000,
  })
  const { currentNetwork } = useNetwork()
  const budgetCurrency = advertiser?.defaultCurrency || currentNetwork?.defaultCurrency || 'EUR'
  // Built once per advertiser: the drafts carry generated row keys, and fresh
  // keys on every render would read as changed default values.
  const politicalDefaults = useMemo(
    () => ({
      politicalBudgets: budgetDrafts(advertiser, budgetCurrency),
      entities: Object.fromEntries(
        ENTITY_TYPES.map((type) => [type, entityDraft(advertiser, type)])
      ) as Record<(typeof ENTITY_TYPES)[number], EntityDraft>,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [advertiser]
  )
  // Inviting someone to fill in the advertiser's details is part of the
  // political workflow, so it is offered to political advertisers once the
  // network has elections. The invite is tied to the owner team, so the
  // backend refuses it without one. Once linked to a political campaign
  // there is nothing left to ask for.
  const showInvite =
    !!advertiser?.advertiserType && elections.length > 0 && !advertiser.politicalCampaignIds.length
  const canInvite = showInvite && !!advertiser?.ownerTeamId
  const isLinked = !!advertiser?.politicalCampaignIds.length
  // The sponsors behind the linked campaigns, named on the linked button.
  const linkedSponsors = useLinkedSponsorNames(advertiser?.politicalCampaigns ?? [])
  const { data: linkRequest } = useQuery({
    queryKey: politicalLinkRequestQueryKey(id),
    queryFn: () => AdvertisersApi.getPoliticalLinkRequest(id),
    enabled: canInvite,
  })
  const { data: teams = [] } = useQuery({
    queryKey: ['teams'],
    queryFn: () => TeamsApi.getAll(),
  })

  const form = useForm({
    defaultValues: {
      name: advertiser?.name ?? '',
      description: advertiser?.description ?? '',
      contact: advertiser?.contact ?? '',
      email: advertiser?.email ?? '',
      phone: advertiser?.phone ?? '',
      legalName: advertiser?.legalName ?? '',
      vatId: advertiser?.vatId ?? '',
      advertiserUrl: advertiser?.advertiserUrl ?? '',
      address: {
        addressLine1: advertiser?.address.addressLine1 ?? '',
        addressLine2: advertiser?.address.addressLine2 ?? '',
        city: advertiser?.address.city ?? '',
        state: advertiser?.address.state ?? '',
        postCode: advertiser?.address.postCode ?? '',
        country: advertiser?.address.country ?? '',
      },
      billingId: advertiser?.billingId ?? '',
      billingName: advertiser?.billingName ?? '',
      billingSurname: advertiser?.billingSurname ?? '',
      billingEmail: advertiser?.billingEmail ?? '',
      billingPhone: advertiser?.billingPhone ?? '',
      ownerTeamId: advertiser?.ownerTeamId ?? '',
      defaultCurrency: advertiser?.defaultCurrency ?? '',
      labels: advertiser?.labels ?? [],
      advertiserType: advertiser?.advertiserType ?? '',
      politicalBudgets: politicalDefaults.politicalBudgets,
      entities: politicalDefaults.entities,
    },
    onSubmit: ({ value }) => {
      mutation.mutate({
        name: value.name,
        description: value.description,
        contact: value.contact,
        email: value.email,
        phone: value.phone,
        legalName: value.legalName,
        vatId: value.vatId,
        advertiserUrl: value.advertiserUrl,
        address: {
          addressLine1: value.address.addressLine1 || undefined,
          addressLine2: value.address.addressLine2 || undefined,
          city: value.address.city || undefined,
          state: value.address.state || undefined,
          postCode: value.address.postCode || undefined,
          country: value.address.country || undefined,
        },
        billingId: value.billingId,
        billingName: value.billingName,
        billingSurname: value.billingSurname,
        billingEmail: value.billingEmail,
        billingPhone: value.billingPhone,
        objectState: (advertiser?.objectState ?? 'ACTIVE') as ObjectState,
        ownerTeam: value.ownerTeamId ? { id: value.ownerTeamId } : undefined,
        defaultCurrency: value.defaultCurrency || undefined,
        labels: value.labels,
        type: value.advertiserType || null,
        // Budgets and entities are only edited for a political advertiser;
        // otherwise they are left out, so the stored ones are kept.
        ...(value.advertiserType
          ? { politicalBudgets: budgetsInput(value.politicalBudgets), entities: entitiesInput(value.entities) }
          : {}),
      })
    },
  })
  const saveObjectState = useSaveObjectState(
    (objectState) => AdvertisersApi.update(id, { name: advertiser?.name ?? '', objectState }),
    ['advertiser', id],
    ['advertisers']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  const mutation = useMutation({
    mutationFn: (input: AdvertiserInput) => AdvertisersApi.update(id, input),
    onSuccess: (data) => {
      if (posthogConfigured) posthog.capture(isNew ? 'advertiser_created' : 'advertiser_updated', {
        has_owner_team: Boolean(data.ownerTeamId),
        is_political: Boolean(data.advertiserType),
      })
      queryClient.setQueryData(['advertiser', id], data)
      queryClient.setQueryData(['advertisers'], (old: Advertiser[] | undefined) => {
        if (!old) return [data]
        const idx = old.findIndex((a) => a.id === data.id)
        return idx >= 0 ? old.map((a) => (a.id === data.id ? data : a)) : [...old, data]
      })
      markClean()
      if (isNew) {
        navigate(`/advertising/advertisers/${id}`, { replace: !createDialog })
      } else {
        stopEditing()
      }
      toast.success(t('common.saved'), { description: isNew ? t('advertisers.createdSuccess') : t('advertisers.savedSuccess'), duration: 3000 })
    },
    onError: (error) => {
      toast.error(isNew ? t('advertisers.createFailed') : t('advertisers.saveFailed'), { description: apiErrorDescription(error), duration: 5000 })
    },
  })

  const crumbs = useObjectCrumbs(
    {},
    isNew ? t('advertisers.newAdvertiser') : (advertiser?.name ?? t('advertisers.editTitle'))
  )

  // Derive warnings from current form values
  const urlValue = useStore(form.store, (s) => s.values.advertiserUrl)
  const advertiserTypeValue = useStore(form.store, (s) => s.values.advertiserType)
  const saveAttempted = useStore(form.store, (s) => s.submissionAttempts > 0)
  // Fills an entity in from the advertiser's own, possibly unsaved, details.
  const copyAdvertiserToEntity = (draft: EntityDraft): EntityDraft => {
    const v = form.state.values
    return {
      ...draft,
      legalName: v.legalName || draft.legalName,
      legalEntity: v.advertiserType || draft.legalEntity,
      contactEmail: v.email || draft.contactEmail,
      url: v.advertiserUrl || draft.url,
      vatId: v.vatId || draft.vatId,
      address: Object.values(v.address).some(Boolean) ? { ...v.address } : draft.address,
    }
  }
  const urlValid = !urlValue || (() => {
    try {
      const u = new URL(urlValue)
      return (u.protocol === 'https:' || u.protocol === 'http:') && u.hostname.includes('.')
    } catch {
      return false
    }
  })()
  // One line per filled-in part, so the read-only card skips what is missing.
  const address = advertiser?.address
  const addressLines = address
    ? [
        address.addressLine1,
        address.addressLine2,
        [address.postCode, address.city].filter(Boolean).join(' '),
        address.state,
        address.country,
      ].filter(Boolean)
    : []
  const billingFullName = [advertiser?.billingName, advertiser?.billingSurname].filter(Boolean).join(' ')
  const hasBilling =
    !!billingFullName || !!advertiser?.billingEmail || !!advertiser?.billingPhone || !!advertiser?.billingId

  // Sections inside the folded-away advanced block that hold data, badged on its toggle.
  const advancedCount = useStore(form.store, (s) => {
    const v = s.values
    const hasAddress = Object.values(v.address).some(Boolean)
    const hasBillingInput =
      !!(v.billingId || v.billingName || v.billingSurname || v.billingEmail || v.billingPhone)
    return Number(hasAddress) + Number(hasBillingInput)
  })

  // Address and billing sit in the advanced block for most advertisers, but a
  // political one shows them outright — its transparency notice needs them.
  const addressAndBillingCards = (
    <>
      <Card tone="people">
        <CardHeader>
          <CardTitle>
            <CardIcon icon={MapPin} />
            {t('address.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="field-grid">
            <div className="p-field full">
              <label>{t('address.line1')}</label>
              <form.Field name="address.addressLine1">
                {(field) => (
                  <Input
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('address.streetPlaceholder')}
                  />
                )}
              </form.Field>
            </div>
            <div className="p-field full">
              <label>{t('address.line2')}</label>
              <form.Field name="address.addressLine2">
                {(field) => (
                  <Input
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('address.aptPlaceholder')}
                  />
                )}
              </form.Field>
            </div>
            <div className="p-field">
              <label>{t('address.city')}</label>
              <form.Field name="address.city">
                {(field) => (
                  <Input
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('address.cityPlaceholder')}
                  />
                )}
              </form.Field>
            </div>
            <div className="p-field">
              <label>{t('address.stateRegion')}</label>
              <form.Field name="address.state">
                {(field) => (
                  <Input
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('address.statePlaceholder')}
                  />
                )}
              </form.Field>
            </div>
            <div className="p-field">
              <label>{t('address.postCode')}</label>
              <form.Field name="address.postCode">
                {(field) => (
                  <Input
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('address.postCodePlaceholder')}
                  />
                )}
              </form.Field>
            </div>
            <div className="p-field">
              <label>{t('address.country')}</label>
              <form.Field name="address.country">
                {(field) => (
                  <Input
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('address.countryPlaceholder')}
                  />
                )}
              </form.Field>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card tone="money">
        <CardHeader>
          <CardTitle>
            <CardIcon icon={Receipt} />
            {t('advertisers.billing')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="field-grid">
            <div className="p-field">
              <label>{t('advertisers.billingName')}</label>
              <form.Field name="billingName">
                {(field) => (
                  <Input
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('advertisers.billingNamePlaceholder')}
                  />
                )}
              </form.Field>
            </div>
            <div className="p-field">
              <label>{t('advertisers.billingSurname')}</label>
              <form.Field name="billingSurname">
                {(field) => (
                  <Input
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('advertisers.billingSurnamePlaceholder')}
                  />
                )}
              </form.Field>
            </div>
            <div className="p-field">
              <label>{t('common.email')}</label>
              <form.Field name="billingEmail">
                {(field) => (
                  <Input
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('advertisers.emailPlaceholder')}
                    type="email"
                  />
                )}
              </form.Field>
            </div>
            <div className="p-field">
              <label>{t('common.phone')}</label>
              <form.Field name="billingPhone">
                {(field) => (
                  <Input
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('advertisers.phonePlaceholder')}
                  />
                )}
              </form.Field>
            </div>
            <div className="p-field full">
              <label>{t('advertisers.billingId')}</label>
              <form.Field name="billingId">
                {(field) => (
                  <Input
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('advertisers.billingIdPlaceholder')}
                  />
                )}
              </form.Field>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  )

  const warnings: string[] = []
  if (!urlValue) warnings.push(t('advertisers.warnNoUrl'))
  else if (!urlValid) warnings.push(t('advertisers.warnInvalidUrl'))

  return (
    <AppShell crumbs={crumbs} showBack>
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={advertiser?.validationWarnings} />

        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.advertiser')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('advertisers.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {!isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              <ObjectStateToggle value={advertiser!.objectState} onChange={saveObjectState} />
              {!isEditing && showInvite && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* A disabled button fires no pointer events, so the span carries the tooltip. */}
                    <span tabIndex={canInvite ? -1 : 0}>
                      <Button type="button" variant="outline" disabled={!canInvite} onClick={() => setInviteOpen(true)}>
                        {linkRequest ? <Clock className="size-3.5" /> : <Link2 className="size-3.5" />}
                        {linkRequest ? t('advertisers.invitePendingButton') : t('advertisers.invite')}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {!canInvite
                      ? t('advertisers.inviteNeedsTeam')
                      : linkRequest
                        ? t('advertisers.invitePendingHelp', { name: linkRequest.name })
                        : t('advertisers.inviteHelp')}
                  </TooltipContent>
                </Tooltip>
              )}
              {!isEditing && isLinked && (
                <Button type="button" onClick={() => setLinkedOpen(true)} className="max-w-80">
                  <Landmark className="size-3.5" />
                  <span className="truncate">
                    {linkedSponsors
                      ? t('advertisers.politicalLinkedTo', { name: linkedSponsors })
                      : t('advertisers.politicalLinked')}
                  </span>
                </Button>
              )}
            </div>
          )}
        </div>

        {isEditing && warnings.length > 0 && (
          <Alert variant="destructive" className="mb-0">
            <TriangleAlert className="size-4" />
            <AlertDescription>
              <ul className="list-disc list-inside space-y-0.5">
                {warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {advertiser && !isNew && (
          <StatsBar
            items={[
              {
                label: t('common.id'),
                content: <StatsBarId value={advertiser.id} />,
              },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(advertiser.createTime)}
                  />
                ),
              },
              {
                label: t('advertisers.last24h'),
                content: <StatsBarTraffic active={advertiser.bidInLastDay} />,
              },
            ]}
          />
        )}

        <>

            {isEditing ? (
              <div className="flex flex-col gap-3.5 lg:flex-row lg:items-start">
                {/* Left: stacked form cards */}
                <div className="flex flex-col gap-3.5 flex-1 min-w-0">
                  {/* Details card */}
                  <Card>
                    <CardHeader>
                      <CardTitle>
                        <CardIcon icon={IdCard} />
                        {t('advertisers.details')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="field-grid">
                        <div className="p-field full">
                          <label>{t('common.description')}</label>
                          <form.Field name="description">
                            {(field) => (
                              <Textarea
                                value={field.state.value}
                                onChange={(e) => field.handleChange(e.target.value)}
                                placeholder={t('advertisers.descriptionPlaceholder')}
                                rows={2}
                                style={{ resize: 'vertical' }}
                              />
                            )}
                          </form.Field>
                        </div>
                        <div className="p-field">
                          <label>{t('advertisers.legalName')}</label>
                          <form.Field name="legalName">
                            {(field) => (
                              <Input
                                value={field.state.value}
                                onChange={(e) => field.handleChange(e.target.value)}
                                placeholder={t('advertisers.legalNamePlaceholder')}
                              />
                            )}
                          </form.Field>
                        </div>
                        <div className="p-field">
                          <label>{t('political.vatId')}</label>
                          <form.Field name="vatId">
                            {(field) => (
                              <Input
                                value={field.state.value}
                                onChange={(e) => field.handleChange(e.target.value)}
                                placeholder={t('political.vatIdPlaceholder')}
                              />
                            )}
                          </form.Field>
                        </div>
                        <div className="p-field">
                          <label style={!urlValue || !urlValid ? { color: 'var(--ad-danger)' } : undefined}>
                            {t('common.website')}
                          </label>
                          <form.Field name="advertiserUrl">
                            {(field) => (
                              <Input
                                value={field.state.value}
                                onChange={(e) => field.handleChange(e.target.value)}
                                placeholder={t('advertisers.websitePlaceholder')}
                                className={!field.state.value || !urlValid ? 'border-(--ad-danger) focus-visible:ring-(--ad-danger)' : ''}
                              />
                            )}
                          </form.Field>
                        </div>
                        <div className="p-field full">
                          <label>{t('common.team')}</label>
                          <form.Field name="ownerTeamId">
                            {(field) => (
                              <SearchSelect
                                value={field.state.value}
                                onChange={field.handleChange}
                                options={teams.map((team) => ({ value: team.id, label: team.name }))}
                                placeholder={t('advertisers.selectTeam')}
                                icon={Users}
                              />
                            )}
                          </form.Field>
                        </div>
                        <div className="p-field">
                          <label>{t('advertisers.defaultCurrency')}</label>
                          <form.Field name="defaultCurrency">
                            {(field) => (
                              <Select
                                label={t('advertisers.defaultCurrency')}
                                value={field.state.value}
                                onValueChange={(v) => field.handleChange(v ?? '')}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder={t('advertisers.defaultCurrencyPlaceholder')}>
                                    {field.state.value || t('advertisers.defaultCurrencyPlaceholder')}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  {['USD', 'EUR', 'GBP', 'NOK', 'SEK', 'DKK', 'AUD', 'CAD', 'JPY', 'CHF'].map((c) => (
                                    <SelectItem key={c} value={c}>{c}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </form.Field>
                        </div>
                        <div className="p-field">
                          <label>{t('advertisers.political')}</label>
                          <form.Field name="advertiserType">
                            {(field) => (
                              <div className="flex items-center gap-2 h-9">
                                <Switch
                                  checked={!!field.state.value}
                                  onCheckedChange={(v) => field.handleChange(v ? 'CORPORATE_ENTITY_PUBLIC' : '')}
                                />
                                <span className="text-sm text-muted-foreground">
                                  {field.state.value ? t('advertisers.politicalOn') : t('advertisers.politicalOff')}
                                </span>
                              </div>
                            )}
                          </form.Field>
                        </div>
                        {advertiserTypeValue && (
                          <div className="p-field">
                            <label>{t('advertisers.entityType')}</label>
                            <form.Field name="advertiserType">
                              {(field) => (
                                <Select
                                  label={t('advertisers.entityType')}
                                  value={field.state.value}
                                  onValueChange={(v) => field.handleChange(v ?? '')}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder={t('advertisers.selectEntityType')}>
                                      {({
                                        CORPORATE_ENTITY_PUBLIC: t('political.legalPublic'),
                                        CORPORATE_ENTITY_PRIVATE: t('political.legalPrivate'),
                                        POLITICAL_PARTY: t('political.legalParty'),
                                        INDIVIDUAL: t('political.legalIndividual'),
                                      } as Record<string, string>)[field.state.value] ?? t('advertisers.selectEntityType')}
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="CORPORATE_ENTITY_PUBLIC">{t('political.legalPublic')}</SelectItem>
                                    <SelectItem value="CORPORATE_ENTITY_PRIVATE">{t('political.legalPrivate')}</SelectItem>
                                    <SelectItem value="POLITICAL_PARTY">{t('political.legalParty')}</SelectItem>
                                    <SelectItem value="INDIVIDUAL">{t('political.legalIndividual')}</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                            </form.Field>
                          </div>
                        )}
                        <div className="p-field full">
                          <label>{t('common.labels')}</label>
                          <form.Field name="labels">
                            {(field) => (
                              <LabelAutocomplete
                                value={field.state.value}
                                onChange={(v) => field.handleChange(v)}
                              />
                            )}
                          </form.Field>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Contact card */}
                  <Card tone="people">
                    <CardHeader>
                      <CardTitle>
                        <CardIcon icon={Mail} />
                        {t('advertisers.contact')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="field-grid">
                        <div className="p-field full">
                          <label>{t('advertisers.contactPerson')}</label>
                          <form.Field name="contact">
                            {(field) => (
                              <Input
                                value={field.state.value}
                                onChange={(e) => field.handleChange(e.target.value)}
                                placeholder={t('advertisers.contactPlaceholder')}
                              />
                            )}
                          </form.Field>
                        </div>
                        <div className="p-field">
                          <label>{t('common.email')}</label>
                          <form.Field name="email">
                            {(field) => (
                              <Input
                                value={field.state.value}
                                onChange={(e) => field.handleChange(e.target.value)}
                                placeholder={t('advertisers.emailPlaceholder')}
                                type="email"
                              />
                            )}
                          </form.Field>
                        </div>
                        <div className="p-field">
                          <label>{t('common.phone')}</label>
                          <form.Field name="phone">
                            {(field) => (
                              <Input
                                value={field.state.value}
                                onChange={(e) => field.handleChange(e.target.value)}
                                placeholder={t('advertisers.phonePlaceholder')}
                              />
                            )}
                          </form.Field>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {advertiserTypeValue && (
                    <Card tone="attention">
                      <CardHeader>
                        <CardTitle>
                          <CardIcon icon={Coins} />
                          {t('advertisers.politicalBudgets')}
                        </CardTitle>
                        <div className="ad-card__subtitle">
                          {isLinked ? t('advertisers.politicalBudgetsLinkedHelp') : t('advertisers.politicalBudgetsHelp')}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <form.Field
                          name="politicalBudgets"
                          validators={{
                            onChange: ({ value }) =>
                              Object.keys(budgetIssues(value)).length ? t('advertisers.politicalBudgetsInvalid') : undefined,
                            onSubmit: ({ value }) =>
                              Object.keys(budgetIssues(value)).length ? t('advertisers.politicalBudgetsInvalid') : undefined,
                          }}
                        >
                          {(field) => (
                            <>
                              <AdvertiserPoliticalBudgetsEditor
                                value={field.state.value}
                                onChange={field.handleChange}
                                defaultCurrency={budgetCurrency}
                                showIssues={saveAttempted}
                              />
                              {saveAttempted && field.state.meta.errors[0] && (
                                <p className="mt-2 text-xs text-destructive">{field.state.meta.errors[0] as string}</p>
                              )}
                            </>
                          )}
                        </form.Field>
                      </CardContent>
                    </Card>
                  )}

                  {/* Address and billing are rarely needed, so they start folded away —
                      except for a political advertiser, whose transparency notice needs them. */}
                  {advertiserTypeValue ? (
                    addressAndBillingCards
                  ) : (
                    <AdvancedSection open={advancedOpen} onOpenChange={setAdvancedOpen} activeCount={advancedCount}>
                      {addressAndBillingCards}
                    </AdvancedSection>
                  )}

                  {advertiserTypeValue && (
                    <Card tone="attention">
                      <CardHeader>
                        <CardTitle>
                          <CardIcon icon={Landmark} />
                          {t('advertisers.politicalEntities')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-col gap-4">
                          {ENTITY_TYPES.map((type) => (
                            <form.Field key={type} name={`entities.${type}`}>
                              {(field) => (
                                <AdvertiserEntityFields
                                  type={type}
                                  value={field.state.value}
                                  onChange={field.handleChange}
                                  onCopyFromAdvertiser={() => field.handleChange(copyAdvertiserToEntity(field.state.value))}
                                />
                              )}
                            </form.Field>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Right: stats panel */}
                <div className="order-first min-w-0 flex-1 lg:order-none">
                  <StatsPanel
                    query={{ advertiserId: advertiser?.id ?? id, groupBy: 'HALF_HOURLY' }}
                    storageKey={`advertiser-stats-${id}`}
                    variant="advertising"
                    auditObject={advertiser ? { type: 'advertiser', id: advertiser.id } : undefined}
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3.5 lg:flex-row lg:items-stretch">
                {/* Left: info card */}
                {advertiser && (
                  <Card surface="highlight" className="flex-1 min-w-0">
                    <ListCardHeader
                      title={
                        <>
                          <CardIcon icon={advertiser.advertiserType ? Landmark : IdCard} />
                          {t('advertisers.details')}
                          {advertiser.advertiserType && <PoliticalBadge />}
                        </>
                      }
                      actions={
                        <Button type="button" variant="tinted" size="sm" onClick={startEditing}>
                          <Pencil className="size-3.5" />
                          {t('common.edit')}
                        </Button>
                      }
                    />
                    <CardContent>
                      <DetailList>
                        <DetailRow label={t('common.website')} icon={Globe}>
                          {advertiser.advertiserUrl && (
                            <a href={advertiser.advertiserUrl} target="_blank" rel="noopener noreferrer" className="block truncate text-primary hover:underline">
                              {advertiser.advertiserUrl}
                            </a>
                          )}
                        </DetailRow>
                        <DetailRow label={t('advertisers.legalName')} icon={Building2}>
                          {advertiser.legalName}
                        </DetailRow>
                        <DetailRow label={t('political.vatId')} icon={Receipt}>
                          {advertiser.vatId}
                        </DetailRow>
                        <DetailRow label={t('common.team')} icon={Users}>
                          {advertiser.ownerTeamId && (teams.find((tm) => tm.id === advertiser.ownerTeamId)?.name ?? advertiser.ownerTeamId)}
                        </DetailRow>
                        <DetailRow label={t('advertisers.contactPerson')} icon={User}>
                          {advertiser.contact}
                        </DetailRow>
                        <DetailRow label={t('common.email')} icon={Mail}>
                          {advertiser.email && (
                            <a href={`mailto:${advertiser.email}`} className="text-primary hover:underline">{advertiser.email}</a>
                          )}
                        </DetailRow>
                        <DetailRow label={t('common.phone')} icon={Phone}>
                          {advertiser.phone && (
                            <a href={`tel:${advertiser.phone}`} className="text-primary hover:underline">{advertiser.phone}</a>
                          )}
                        </DetailRow>
                        <DetailRow label={t('address.title')} icon={MapPin}>
                          {addressLines.length > 0 && (
                            <span className="whitespace-pre-line">{addressLines.join('\n')}</span>
                          )}
                        </DetailRow>
                        <DetailRow label={t('advertisers.billing')} icon={Receipt}>
                          {hasBilling && (
                            <span className="flex flex-col">
                              {billingFullName && <span>{billingFullName}</span>}
                              {advertiser.billingEmail && (
                                <a href={`mailto:${advertiser.billingEmail}`} className="text-primary hover:underline">{advertiser.billingEmail}</a>
                              )}
                              {advertiser.billingPhone && (
                                <a href={`tel:${advertiser.billingPhone}`} className="text-primary hover:underline">{advertiser.billingPhone}</a>
                              )}
                              {advertiser.billingId && (
                                <span className="text-muted-foreground">{t('advertisers.billingId')}: {advertiser.billingId}</span>
                              )}
                            </span>
                          )}
                        </DetailRow>
                        <DetailRow label={t('common.description')} icon={FileText}>
                          {advertiser.description && (
                            <span className="text-muted-foreground">{advertiser.description}</span>
                          )}
                        </DetailRow>
                        <DetailRow label={t('advertisers.defaultCurrency')} icon={Coins}>
                          {advertiser.defaultCurrency}
                        </DetailRow>
                        <DetailRow label={t('advertisers.entityType')} icon={Landmark}>
                          {advertiser.advertiserType && (
                            <span className="font-medium text-(--ad-primary)">
                              {({
                                CORPORATE_ENTITY_PUBLIC: t('political.legalPublic'),
                                CORPORATE_ENTITY_PRIVATE: t('political.legalPrivate'),
                                POLITICAL_PARTY: t('political.legalParty'),
                                INDIVIDUAL: t('political.legalIndividual'),
                              } as Record<string, string>)[advertiser.advertiserType] ?? advertiser.advertiserType}
                            </span>
                          )}
                        </DetailRow>
                        <DetailRow label={t('common.labels')} icon={Tag}>
                          {advertiser.labels.length > 0 && (
                            <span className="flex flex-wrap gap-1">
                              {advertiser.labels.map((label) => (
                                <span key={label} className="inline-flex items-center gap-1 rounded-full border border-(--ad-badge-blue-border) bg-(--ad-badge-blue-bg) px-2 py-0.5 text-xs font-medium text-(--ad-badge-blue)">
                                  <Tag className="size-2.5" />
                                  {label}
                                </span>
                              ))}
                            </span>
                          )}
                        </DetailRow>
                      </DetailList>
                    </CardContent>
                  </Card>
                )}
                {/* Right: stats panel */}
                <div className="order-first min-w-0 flex-1 lg:order-none">
                  <StatsPanel
                    query={{ advertiserId: advertiser?.id ?? id, groupBy: 'HALF_HOURLY' }}
                    storageKey={`advertiser-stats-${id}`}
                    variant="advertising"
                    auditObject={advertiser ? { type: 'advertiser', id: advertiser.id } : undefined}
                  />
                </div>
              </div>
            )}

            {!isEditing &&
              advertiser?.advertiserType &&
              (advertiser.politicalBudgets.length > 0 || advertiser.entities.length > 0) && (
                <AdvertiserPoliticalSummaryCard advertiser={advertiser} />
              )}

            {!isEditing && advertiser && advertiser.politicalCampaigns.length > 0 && (
              <AdvertiserTransparencyCard campaigns={advertiser.politicalCampaigns} />
            )}

            {/* Save bar */}
            <div className={`detail-savebar${isEditing || isDirty || mutation.isPending ? ' is-visible' : ''}`}>
              <div className="detail-savebar__left">
                {mutation.isPending && (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    {isNew ? t('common.creating') : t('common.saving')}
                  </>
                )}
              </div>
              <div className="detail-savebar__actions">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => (isNew ? cancelNew('/advertising/advertisers') : stopEditing())}
                  disabled={mutation.isPending}
                >
                  <X className="size-4" />
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  loading={mutation.isPending}
                >
                  {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
                  {isNew ? t('advertisers.newAdvertiser') : t('common.save')}
                </Button>
              </div>
            </div>
          {!isNew && <AdvertiserRelatedPanel advertiserId={id} ownerTeamId={advertiser?.ownerTeamId} />}
        </>
      </form>
      <UnsavedChangesDialog blocker={blocker} />
      {advertiser && canInvite && (
        <AdvertiserInviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} advertiser={advertiser} />
      )}
      {advertiser && isLinked && (
        <AdvertiserPoliticalLinkDialog open={linkedOpen} onClose={() => setLinkedOpen(false)} advertiser={advertiser} />
      )}
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function AdvertiserEditPage() {
  const { t } = useTranslation()
  const { id, searchParams } = useEditRoute()
  const isNew = searchParams.get('new') === 'true'
  const hasToken = AuthService.isAuthenticated()

  const { data: advertiser, isLoading, isError } = useQuery({
    queryKey: ['advertiser', id],
    queryFn: () => AdvertisersApi.getById(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })

  const crumbsLoading = useObjectCrumbs(
    {},
    isNew ? t('advertisers.newAdvertiser') : t('advertisers.editTitle')
  )

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if (isLoading && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={3} columns={2} />
      </AppShell>
    )
  }

  return (
    <AdvertiserEditForm
      key={id}
      advertiser={advertiser ?? null}
      id={id!}
      isNew={isNew}
    />
  )
}
